import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useSupabaseClient } from "@/hooks/use-supabase-client";
import { positionForIndex } from "@/lib/block-position";
import type { SiteTemplate } from "@/lib/templates/types";
import type { Json } from "@/integrations/supabase/types";

interface ApplyTemplateArgs {
  siteId: string;
  template: SiteTemplate;
  unitName: string;
  unitIdentifier: string;
}

export function useSiteTemplates() {
  const getClient = useSupabaseClient();
  const queryClient = useQueryClient();

  const applyTemplate = useMutation({
    mutationFn: async ({ siteId, template, unitName, unitIdentifier }: ApplyTemplateArgs): Promise<void> => {
      const supabase = await getClient();

      const { error: themeError } = await supabase
        .from("sites" as any)
        .update({ theme_config: template.theme } as any)
        .eq("id", siteId);
      if (themeError) throw themeError;

      const pages = template.buildPages(unitName, unitIdentifier);
      for (const page of pages) {
        // Seed content's landing page is always slug 'home' (see
        // seed-content.ts) — this is the one place that convention still
        // matters, since it's what makes a freshly-templated site work
        // without the unit having to pick a home page themselves. `is_home`
        // predates this repo's last `supabase gen types` refresh, same
        // stopgap cast as block_type below.
        const { data: createdPage, error: pageError } = await supabase
          .from("pages")
          .insert({
            site_id: siteId,
            title: page.title,
            slug: page.slug,
            page_order: page.page_order,
            is_home: page.slug === "home",
          } as any)
          .select()
          .single();
        if (pageError) throw pageError;

        if (page.blocks.length > 0) {
          // block_type's generated union predates leadership/unit-identity —
          // same stopgap cast used elsewhere for columns added since this
          // repo's last `supabase gen types` refresh (see CLAUDE.md).
          const { error: blocksError } = await supabase.from("blocks").insert(
            page.blocks.map((block) => {
              // Template blocks each start as their own full-width row —
              // row_order and block_order share the same fractional
              // position, same as any other single-block row.
              const order = positionForIndex(block.block_order);
              return {
                page_id: createdPage.id,
                block_type: block.block_type,
                content: block.content as unknown as Json,
                block_order: order,
                row_order: order,
                col_span: 12,
              };
            }) as any
          );
          if (blocksError) throw blocksError;
        }
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["site"] });
      void queryClient.invalidateQueries({ queryKey: ["pages"] });
    },
  });

  return {
    applyTemplate: (args: ApplyTemplateArgs) => applyTemplate.mutateAsync(args),
    applying: applyTemplate.isPending,
    error: applyTemplate.error instanceof Error ? applyTemplate.error.message : null,
  };
}
